import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeftIcon, Save, X } from "lucide-react";
import { DatePicker, Select, TimePicker } from "antd";
import dayjs from "dayjs";

import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import {
    TRAINING_TASKS,
    EMPLOYEE_DEPARTMENT,
    EMPLOYEE_PERSONAL_INFO,
} from "../../constants/api";

const INITIAL = {
    title: "",
    description: "",
    instructions: "",
    due_date: null,
    due_time: null,
    priority: "medium",
    employee_ids: [],
    department_ids: [],
};

const fieldClass = cn(
    "w-full rounded-md transition-colors duration-200",
    "border-gray-300 dark:border-gray-600",
    "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
    "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
);

const labelClass = "text-sm font-medium text-gray-700 dark:text-gray-300";

const TrainingAddEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const editMode = !!id;
    const { fetch, create, update } = useApiMethods();

    // Only admins/super-admins may create or edit trainings. Guards direct
    // navigation to /training/add or /training/edit/:id (the list already
    // hides the entry button for non-admins).
    const isAdmin = JSON.parse(sessionStorage.getItem("is_admin") || "false")
        || JSON.parse(sessionStorage.getItem("is_super_admin") || "false");

    const [form, setForm] = useState(INITIAL);
    const [departments, setDepartments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [assignBy, setAssignBy] = useState("employee");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadOptions = useCallback(async () => {
        try {
            const [deptRes, empRes] = await Promise.all([
                fetch(EMPLOYEE_DEPARTMENT, { drop_down: "True" }),
                fetch(EMPLOYEE_PERSONAL_INFO, { drop_down: "True" }),
            ]);
            setDepartments(deptRes?.data || []);
            setEmployees((empRes?.data || []).filter((e) => e.is_active));
        } catch (_) { /* non-fatal */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadTask = useCallback(async () => {
        if (!editMode) return;
        setLoading(true);
        try {
            const res = await fetch(TRAINING_TASKS, { id });
            const data = res?.data;
            if (data) {
                setForm({
                    title: data.title || "",
                    description: data.description || "",
                    instructions: data.instructions || "",
                    due_date: data.due_date ? dayjs(data.due_date) : null,
                    due_time: data.due_time ? dayjs(data.due_time, "HH:mm:ss") : null,
                    priority: data.priority || "medium",
                    employee_ids: (data.assignments || []).map((a) => a.employee_id),
                    department_ids: [],
                });
            }
        } catch (_) {
            ToastMsg("error", "Failed to load training");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, editMode]);

    useEffect(() => {
        loadOptions();
        loadTask();
    }, [loadOptions, loadTask]);

    // Redirect non-admins who reach this page by direct URL.
    useEffect(() => {
        if (!isAdmin) navigate("/training", { replace: true });
    }, [isAdmin, navigate]);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const employeesInSelectedDepartments = useMemo(() => {
        if (!form.department_ids.length) return [];
        const set = new Set(form.department_ids);
        return employees.filter((e) => {
            // The employee `info` endpoint returns department_id as an array
            // [{ id, department_name }]; fall back to plain shapes too.
            const deptId = Array.isArray(e.department_id)
                ? e.department_id?.[0]?.id
                : (e.department?.id ?? e.department_id);
            return deptId != null && set.has(deptId);
        });
    }, [employees, form.department_ids]);

    const previewAssigneeCount = useMemo(() => {
        const ids = new Set(form.employee_ids);
        employeesInSelectedDepartments.forEach((e) => ids.add(e.id));
        return ids.size;
    }, [form.employee_ids, employeesInSelectedDepartments]);

    const handleSave = async () => {
        if (!isAdmin) {
            ToastMsg("error", "You don't have permission to manage trainings");
            return;
        }
        if (!form.title.trim()) {
            ToastMsg("error", "Title is required");
            return;
        }
        if (!form.due_date) {
            ToastMsg("error", "Due date is required");
            return;
        }
        if (!form.employee_ids.length && !form.department_ids.length) {
            ToastMsg("error", "Please assign at least one employee or department");
            return;
        }

        const payload = {
            title: form.title.trim(),
            description: form.description,
            instructions: form.instructions,
            due_date: form.due_date.format("YYYY-MM-DD"),
            due_time: form.due_time ? form.due_time.format("HH:mm:ss") : null,
            priority: form.priority,
            employee_ids: form.employee_ids,
            department_ids: form.department_ids,
        };

        setSaving(true);
        try {
            let res;
            if (editMode) {
                res = await update(TRAINING_TASKS, { id, ...payload });
            } else {
                res = await create(TRAINING_TASKS, payload);
            }
            if (res?.status === "success") {
                ToastMsg("success", res.message || (editMode ? "Training updated" : "Training assigned"));
                navigate("/training");
            } else {
                ToastMsg("error", res?.message || "Failed to save");
            }
        } catch (_) {
            ToastMsg("error", "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <ContainerCard>
                <div className="h-[calc(100vh-100px)] flex items-center justify-center rounded-md">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
                </div>
            </ContainerCard>
        );
    }

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <ChevronLeftIcon
                    className="h-5 w-5 cursor-pointer"
                    onClick={() => navigate(-1)}
                />
                <div className="flex-1">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {editMode ? "Edit Training" : "Assign Training"}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {editMode
                            ? "Update existing training details and assignees."
                            : "Set training details and pick assignees."}
                    </p>
                </div>
            </div>

            {/* Form */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6 space-y-8">
                {/* Basic Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    <div className="space-y-2 sm:col-span-2 lg:col-span-2">
                        <label className={labelClass}>
                            Title <span className="text-red-500">*</span>
                        </label>
                        <Input
                            value={form.title}
                            onChange={(e) => handleChange("title", e.target.value)}
                            placeholder="e.g. Hand Hygiene Protocol Training"
                            className={fieldClass}
                        />
                    </div>

                    <div className="space-y-2">
                        <label className={labelClass}>
                            Due Date <span className="text-red-500">*</span>
                        </label>
                        <DatePicker
                            value={form.due_date}
                            onChange={(v) => handleChange("due_date", v)}
                            size="large"
                            className={fieldClass}
                            format="DD-MM-YYYY"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className={labelClass}>Due Time</label>
                        <TimePicker
                            value={form.due_time}
                            onChange={(v) => handleChange("due_time", v)}
                            size="large"
                            className={fieldClass}
                            format="HH:mm"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className={labelClass}>Priority</label>
                        <Select
                            value={form.priority}
                            onChange={(v) => handleChange("priority", v)}
                            size="large"
                            className={fieldClass}
                            options={[
                                { value: "low", label: "Low" },
                                { value: "medium", label: "Medium" },
                                { value: "high", label: "High" },
                            ]}
                        />
                    </div>

                    <div className="space-y-2 sm:col-span-2 md:col-span-3 lg:col-span-4">
                        <label className={labelClass}>Description</label>
                        <Textarea
                            value={form.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                            placeholder="Short summary of the training..."
                            rows={3}
                            className={fieldClass}
                        />
                    </div>

                    <div className="space-y-2 sm:col-span-2 md:col-span-3 lg:col-span-4">
                        <label className={labelClass}>Instructions</label>
                        <Textarea
                            value={form.instructions}
                            onChange={(e) => handleChange("instructions", e.target.value)}
                            placeholder="Steps the employee should follow..."
                            rows={4}
                            className={fieldClass}
                        />
                    </div>
                </div>

                {/* Assignees */}
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4 pt-4">
                        <h3 className="font-semibold text-gray-900 dark:text-white">Assignees</h3>
                        <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setAssignBy("employee")}
                                className={cn(
                                    "px-3 py-1.5 text-sm transition-colors",
                                    assignBy === "employee"
                                        ? "bg-sidebar-primary text-white"
                                        : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                                )}
                            >
                                By Employee
                            </button>
                            <button
                                type="button"
                                onClick={() => setAssignBy("department")}
                                className={cn(
                                    "px-3 py-1.5 text-sm transition-colors border-l border-gray-200 dark:border-gray-600",
                                    assignBy === "department"
                                        ? "bg-sidebar-primary text-white"
                                        : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                                )}
                            >
                                By Department
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="space-y-2 lg:col-span-2">
                            <label className={labelClass}>
                                {assignBy === "employee" ? "Select Employees" : "Select Departments"}
                                <span className="text-red-500"> *</span>
                            </label>
                            {assignBy === "employee" ? (
                                <Select
                                    mode="multiple"
                                    value={form.employee_ids}
                                    onChange={(v) => handleChange("employee_ids", v)}
                                    placeholder="Search and select employees"
                                    showSearch
                                    optionFilterProp="label"
                                    maxTagCount={3}
                                    size="large"
                                    className={fieldClass}
                                    options={employees.map((e) => ({
                                        value: e.id,
                                        label: `${e.first_name || ""} ${e.last_name || ""}`.trim(),
                                    }))}
                                />
                            ) : (
                                <Select
                                    mode="multiple"
                                    value={form.department_ids}
                                    onChange={(v) => handleChange("department_ids", v)}
                                    placeholder="Pick one or more departments"
                                    showSearch
                                    optionFilterProp="label"
                                    maxTagCount={3}
                                    size="large"
                                    className={fieldClass}
                                    options={departments.map((d) => ({
                                        value: d.id,
                                        label: d.department_name,
                                    }))}
                                />
                            )}
                            {assignBy === "department" && form.department_ids.length > 0 && (
                                <p className="text-xs text-muted-foreground">
                                    Will assign to {employeesInSelectedDepartments.length} active employees in selected departments.
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <label className={labelClass}>Preview</label>
                            <div className="flex items-center gap-3 p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                <div className="flex flex-col">
                                    <span className="text-xs text-muted-foreground">Total assignees</span>
                                    <span className="text-2xl font-semibold text-sidebar-primary">{previewAssigneeCount}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button variant="outline" onClick={() => navigate("/training")}>
                        <X className="h-4 w-4 mr-1" /> Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={saving || !isAdmin} className="bg-sidebar text-primary-foreground hover:bg-sidebar-primary">
                        <Save className="h-4 w-4 mr-1" /> {editMode ? "Update Training" : "Assign Training"}
                    </Button>
                </div>
            </div>
        </ContainerCard>
    );
};

export default TrainingAddEdit;
